import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Sparkles, Rss, Store, TrendingUp, Wallet, BarChart3, User, Settings, HelpCircle, Briefcase, Users, LayoutDashboard, Navigation2, Grid3X3 } from 'lucide-react';
import { PageOnboardingTooltip, OnboardingSlide } from '@/components/onboarding/PageOnboardingTooltip';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@/contexts/UserContext';
import { useEnhancedMobile } from '@/hooks/use-enhanced-mobile';

interface NavButton {
  icon: React.ElementType;
  label: string;
  description: string;
  path: string;
}

const getNavButtons = (accountType: string | null): NavButton[] => {
  const common: NavButton[] = [
    { icon: Wallet, label: 'Wallet', description: 'Manage funds & payments', path: '/wallet' },
    { icon: User, label: 'Profile', description: 'View & edit your profile', path: '/profile' },
    { icon: Settings, label: 'Settings', description: 'Account preferences', path: '/settings' },
    { icon: HelpCircle, label: 'Help', description: 'Guides & support', path: '/help' },
  ];

  if (accountType === 'brand') {
    return [
      { icon: Rss, label: 'Signal', description: 'News & updates feed', path: '/feed' },
      { icon: Briefcase, label: 'Deal Management', description: 'Manage your deals', path: '/brand-dashboard' },
      { icon: Users, label: 'Talent Marketplace', description: 'Discover talent', path: '/talent-marketplace' },
      ...common,
    ];
  }

  if (accountType === 'venture') {
    return [
      { icon: LayoutDashboard, label: 'Venture Dashboard', description: 'Portfolio overview', path: '/venture-dashboard' },
      { icon: Briefcase, label: 'Deal Dashboard', description: 'Manage deals', path: '/brand-dashboard' },
      { icon: Rss, label: 'Signal', description: 'News & updates feed', path: '/feed' },
      { icon: Users, label: 'Talent Marketplace', description: 'Discover talent', path: '/talent-marketplace' },
      ...common,
    ];
  }

  // Talent & Agent
  return [
    { icon: Rss, label: 'Signal', description: 'News & updates feed', path: '/feed' },
    { icon: Store, label: 'Marketplace', description: 'Browse opportunities', path: '/marketplace' },
    { icon: TrendingUp, label: 'Deal Progress', description: 'Track active deals', path: '/deal-progress' },
    { icon: BarChart3, label: 'Holdings', description: 'View your holdings', path: '/holdings' },
    ...common,
  ];
};

export const SliceAI = () => {
  const navigate = useNavigate();
  const { accountType } = useUser();
  const { isMobile, isTablet } = useEnhancedMobile();
  const navButtons = getNavButtons(accountType);

  const sliceAISlides: OnboardingSlide[] = [
    { icon: Sparkles, secondaryIcons: [Navigation2, Grid3X3], title: 'Welcome to SliceAI', description: 'Your central command center — navigate to any part of the platform from here. Each card takes you directly to a key feature.', gradient: 'from-primary/20 to-primary/5' },
    { icon: LayoutDashboard, secondaryIcons: [Rss], title: 'Role-Based Navigation', description: 'The cards shown are tailored to your account type. Talent sees deals & marketplace, brands see campaigns & talent discovery, and ventures see portfolio tools.', gradient: 'from-blue-500/20 to-blue-500/5' },
    { icon: Wallet, secondaryIcons: [TrendingUp], title: 'Quick Access Everywhere', description: 'Jump to your Wallet, Profile, Settings, or Help anytime. Everything important is one tap away.', gradient: 'from-emerald-500/20 to-emerald-500/5' },
  ];

  return (
    <div className={`w-full max-w-5xl mx-auto ${isMobile ? 'px-2 py-4' : 'px-4 py-12 md:py-16'} relative`}>
      <PageOnboardingTooltip storageKey="sliceai_tooltip_dismissed" slides={sliceAISlides} dialogTitle="SliceAI Walkthrough" />
      <div className={`flex flex-col items-center justify-center ${isMobile ? 'min-h-[40vh]' : 'min-h-[60vh]'} space-y-6 md:space-y-8 relative z-10`}>
        <div className="text-center space-y-4 md:space-y-6 mb-8">
          <h1 className={`${isMobile ? 'text-3xl' : isTablet ? 'text-4xl' : 'text-5xl md:text-6xl lg:text-7xl'} font-extrabold leading-tight`}>
            <span className="text-foreground">
              You define the <span className="text-accent">mission</span>.
            </span>
            <br />
            <span className="text-foreground">
              We deliver the <span className="text-warning">opportunity</span>.
            </span>
          </h1>
        </div>

        <div className={`grid w-full gap-4 ${isMobile ? 'grid-cols-2' : 'grid-cols-3 md:grid-cols-4'}`}>
          {navButtons.map((btn) => (
            <Card
              key={btn.path}
              onClick={() => navigate(btn.path)}
              className="cursor-pointer bg-card hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 border-border/30"
            >
              <CardContent className={`flex flex-col items-center text-center ${isMobile ? 'p-4' : 'p-5'} gap-2`}>
                <btn.icon className="h-6 w-6 text-foreground" />
                <span className="font-bold text-sm text-foreground">{btn.label}</span>
                <span className="text-xs text-muted-foreground leading-tight">{btn.description}</span>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="flex items-center justify-center gap-1 mt-4">
          <Sparkles className="h-3 w-3 text-muted-foreground/40" />
          <p className="text-xs text-muted-foreground/60 italic font-serif">
            Powered by SliceAI
          </p>
        </div>
      </div>
    </div>
  );
};
